import { get } from 'svelte/store';
import { WindowsCustomEventTypes } from '../enums/windows-custom-event.enum';
import type { IConfigStore } from '../interfaces/store-config.interface';
import { ConfigStore } from '../store/config';
import {
  state_alternativeSearchResults,
  state_answerRating,
  state_apiErrors,
  state_busyApi,
  state_conversationResults,
  state_faq,
  state_indicesList,
  state_questionRelatedSearchSuggestions,
  state_searchResult,
  state_searchString,
  state_searchSuggestion,
  state_selectedIndicesList,
  state_showAlternative,
  state_showSearch,
  state_showBadge,
  state_sessionStorage,
  state_fullScreen,
  state_showContactForm,
} from '../store/state';
import { updateConfigStore } from './update-config-store';
import { UserHandler } from '../controllers/handlers/user.handler';
import { SessionStoreHandler } from './session-storage-handler';
import { defaultLogger as logger } from '../telemetry/logger';

/** all window custom events are mapped to csSearch  in window object  */
export function WindowIntegrations() {
  (window as any)['gqa'] = (window as any)['gqa'] || {};

  const userHandler = new UserHandler();

  initWindowListeners();

  /** get calls */
  mapGetState();
  mapGetConfigStore();

  /*** actions */
  mapToggle();
  mapUpdateConfig();
  mapShowToggle();
  mapShowGreetingPopup();
  mapUserProps();

  mapInit();

  function mapGetState(): void {
    (window as any)['gqa'].getState = () => {
      // method returns the current state of the widget
      return {
        showSearch: get(state_showSearch),
        busyApi: get(state_busyApi),
        apiErrors: get(state_apiErrors),
        searchString: get(state_searchString),
        searchResult: get(state_searchResult),
        conversationResults: get(state_conversationResults),
        searchSuggestion: get(state_searchSuggestion),
        faq: get(state_faq),
        indicesList: get(state_indicesList),
        selectedIndicesList: get(state_selectedIndicesList),
        alternativeSearchResults: get(state_alternativeSearchResults),
        showAlternative: get(state_showAlternative),
        answerRating: get(state_answerRating),
        questionRelatedSearchSuggestions: get(
          state_questionRelatedSearchSuggestions
        ),
        showBadge: get(state_showBadge),
        sessionStorage: get(state_sessionStorage),
      };
    };
  }

  function mapGetConfigStore(): void {
    (window as any)['gqa'].getConfig = () => {
      // function returns the current config of the widget
      return { ...get(ConfigStore) };
    };
  }

  function mapToggle() {
    (window as any)['gqa'].toggleView = (show: boolean) => {
      // function update to the toggle state
      // toggleView(show?: boolean)
      window.dispatchEvent(
        new CustomEvent(
          WindowsCustomEventTypes.toggleView,
          show != undefined ? { detail: show } : undefined
        )
      );
    };
  }

  function mapShowToggle() {
    (window as any)['gqa'].showBadge = (show: boolean) => {
      // function update to the visibility of toggle component
      // showBadge(show?: boolean)
      window.dispatchEvent(
        new CustomEvent(
          WindowsCustomEventTypes.showBadge,
          show != undefined ? { detail: show } : undefined
        )
      );
    };
  }

  function mapUpdateConfig() {
    (window as any)['gqa'].updateConfig = (x: IConfigStore) => {
      // function returns the current configuration of the widget
      window.dispatchEvent(
        new CustomEvent(WindowsCustomEventTypes.updateConfig, { detail: x })
      );
    };
  }

  function mapShowGreetingPopup() {
    (window as any)['gqa'].showGreetingPopup = () => {
      if (!get(ConfigStore).greetingOnToggle)
        return console.error('greetingOnToggle is not enabled in config');

      SessionStoreHandler.remove('offGrtTs');

      if (!get(state_showBadge)) {
        return console.error(
          'Greeting popup is not shown as the toggle is not visible. Greeting Session storage is cleared'
        );
      }
      window.dispatchEvent(
        new CustomEvent(WindowsCustomEventTypes.showGreetingPopup)
      );
    };
  }

  function mapUserProps() {
    (window as any)['gqa'].setUserProps = (props: { [key: string]: string}) => {
      for (const key in props) {
        if (Object.prototype.hasOwnProperty.call(props, key)) {
          userHandler.addProperty(key, props[key]);
        }
      }
    };
    (window as any)['gqa'].deleteUserProps = (propKeys: string[]) => {
      for (const key of propKeys) {
        userHandler.removeProperty(key);
      }
    };
    (window as any)['gqa'].getUserProps = () => {
      return userHandler.getAllProperties();
    };

  }

  function mapInit() {
    (window as any)['gqa'].init = (intiParams: {
      configOptions: IConfigStore;
      serverConfig: {
        config: string;
        tenant: string;
      };
    }) => {
      if (!intiParams?.configOptions?.apiBaseUrl)
        return console.error(`Invalid argument to initiate the search. apiBaseUrl is required.
                ex: {configOptions: {apiBaseUrl: 'xxxx'}}`);

      // function returns the current configuration of the widget
      window.dispatchEvent(
        new CustomEvent(WindowsCustomEventTypes.requestPostOption, {
          detail: intiParams,
        })
      );

      (window as any)['gqa'].init = () => {
        return console.error('GQA widget already initiated');
      };

      return true;
    };
  }

  /** listed to window even listeners */
  function initWindowListeners() {
    [
      WindowsCustomEventTypes.toggleView,
      WindowsCustomEventTypes.updateConfig,
      WindowsCustomEventTypes.showBadge,
      WindowsCustomEventTypes.showCsatPopup,
      WindowsCustomEventTypes.toggleFullScreen,
      WindowsCustomEventTypes.toggleContactForm,
    ].forEach((evt) =>
      window.addEventListener(evt, initWindowListenersHandler)
    );
  }

  /** method to handle window event listeners */
  function initWindowListenersHandler(e: any): void {
    try {
      switch (e.type) {
        case WindowsCustomEventTypes.toggleView:
          if (!(typeof e?.detail == 'boolean' || e?.detail == undefined)) {
            return console.error(`
                        Invalid argument. Should be empty or boolean.
                        ex: toggleView(true) or toggleView()`);
          }
          const showSearch =
            typeof e?.detail == 'boolean' ? e?.detail : !get(state_showSearch);
          state_showSearch.set(showSearch);
          SessionStoreHandler.update({
            visible: showSearch,
          }); // persist to session storage
          logger.trackEvent('Toggle UI Display', {
            showWidget: showSearch,
            tenant: get(ConfigStore)?.serverConfig?.tenant_id,
          });
          break;
        case WindowsCustomEventTypes.updateConfig:
          updateConfigStore(e.detail);
          // window.dispatchEvent(
          //   new CustomEvent(WindowsCustomEventTypes.requestIndices)
          // );
          window.dispatchEvent(
            new CustomEvent(WindowsCustomEventTypes.requestServerConfig, {
              detail: e?.detail?.serverConfig,
            })
          );
          logger.trackEvent('Update Widget Config', {
            config: e.detail,
            tenant: get(ConfigStore)?.serverConfig?.tenant_id,
          });
          break;
        case WindowsCustomEventTypes.showBadge:
          if (!(typeof e?.detail == 'boolean' || e?.detail == undefined)) {
            return console.error(`
                        Invalid argument. Should be empty or boolean.
                        ex: showToggle(true) or showToggle()`);
          }
          const showBadge =
            typeof e?.detail == 'boolean' ? e?.detail : !get(state_showBadge);
          state_showBadge.set(showBadge);
          logger.trackEvent('Toggle Badge Display', {
            showBadge,
            tenant: get(ConfigStore)?.serverConfig?.tenant_id,
          });
          break;
        case WindowsCustomEventTypes.showCsatPopup:
          if (
            !(
              (e?.detail &&
                typeof e?.detail.isRated === 'boolean' &&
                typeof e?.detail.isNotification === 'boolean' &&
                typeof e?.detail.lengthOfQuestions === 'number') ||
              e?.detail == undefined
            )
          ) {
            return console.error(`
                        Invalid argument. Should be empty or boolean.
                        ex: showCsatPopup({isRated: true, isNotification: false, lengthOfQuestions: 5}) or showCsatPopup()`);
          }

          state_sessionStorage.update((store) => {
            return {
              ...store,
              csatRating: e?.detail,
            };
          });
          SessionStoreHandler.update({
            csatRating: {
              ...e?.detail,
              isNotification: false,
            },
          });
          logger.trackEvent('Toggle CSAT rating Display', {});
          break;
        case WindowsCustomEventTypes.toggleFullScreen:
          if (!(typeof e?.detail == 'boolean' || e?.detail == undefined)) {
            return console.error(`
                        Invalid argument. Should be empty or boolean.
                        ex: toggleFullScreen(true) or toggleFullScreen()`);
          }
          const fullscreen =
            typeof e?.detail == 'boolean' ? e?.detail : !get(state_fullScreen);
          state_fullScreen.set(fullscreen);
          SessionStoreHandler.update({
            fullScreen: fullscreen,
          }); // persist to session storage
          logger.trackEvent('Toggle UI fullscreen Display', {});
          break;

        case WindowsCustomEventTypes.toggleContactForm:
          if (!(typeof e?.detail == 'boolean' || e?.detail == undefined)) {
            return console.error(`
                        Invalid argument. Should be empty or boolean.
                        ex: toggleContactForm(true) or toggleContactForm()`);
          }
          const contactForm =
            typeof e?.detail == 'boolean'
              ? e?.detail
              : !get(state_showContactForm);

          state_showContactForm.set(contactForm);

          SessionStoreHandler.update({
            contactForm: contactForm,
          }); // persist to session storage

          logger.trackEvent('Toggle UI contact form Display', {});
          break;
        default:
          break;
      }
    } catch (e) {
      console.error(e);
    }
  }
}
